import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { resolveMedia } from "@/lib/api";
import { FocalImage } from "@/components/FocalImage";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { CartSheet } from "@/components/shop/CartSheet";
import useSEO from "@/lib/useSEO";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);

export { AVAIL_LABELS, AVAIL_COLORS, availOf, isPurchasable } from "@/lib/shopUtils";
import { AVAIL_LABELS, AVAIL_COLORS, availOf, isPurchasable } from "@/lib/shopUtils";

export function AvailBadge({ product: p, testId }) {
  const a = availOf(p);
  const lead = p.lead_time || p.lead_days || "";
  return (
    <span className={`inline-block text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${AVAIL_COLORS[a]}`} data-testid={testId}>
      {AVAIL_LABELS[a]}{a === "da_ordinare" && lead ? ` · ${lead}` : ""}
    </span>
  );
}

const SORTS = [
  ["new", "Novità"],
  ["price_asc", "Prezzo crescente"],
  ["price_desc", "Prezzo decrescente"],
];
const PAGE = 9;

function ProductCard({ p, onAdd }) {
  const gallery = (p.gallery || []).map((g) => (typeof g === "string" ? g : g.url)).filter(Boolean);
  const second = gallery[0];
  return (
    <div className="group" data-testid={`product-${p.id}`}>
      <Link to={`/shop/${p.id}`} className="block relative overflow-hidden bg-white" data-testid={`product-link-${p.id}`}>
        <img src={resolveMedia(p.image)} alt={p.name} loading="lazy"
          className={`w-full h-[340px] md:h-[380px] object-cover transition-all duration-700 group-hover:scale-105 ${second ? "group-hover:opacity-0" : ""}`} />
        {second && (
          <img src={resolveMedia(second)} alt="" aria-hidden loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {p.is_new && <span className="bg-gold text-ink text-[10px] tracking-[0.14em] uppercase px-2 py-1" data-testid={`product-new-${p.id}`}>Novità</span>}
          {p.compare_price > p.price && <span className="bg-ink text-bone text-[10px] tracking-[0.14em] uppercase px-2 py-1">Offerta</span>}
        </div>
      </Link>
      <div className="mt-5 flex justify-between items-start gap-4">
        <div className="min-w-0">
          <div className="text-xs tracking-[0.14em] uppercase text-gold">{p.category}</div>
          <Link to={`/shop/${p.id}`} className="font-serif text-2xl mt-1 block hover:text-gold transition-colors line-clamp-2">{p.name}</Link>
          {p.price_on_request ? (
            <div className="text-sm text-gold mt-1" data-testid={`product-por-${p.id}`}>Prezzo esclusivo su richiesta</div>
          ) : (
            <div className="text-sm mt-1">
              {p.compare_price > p.price && <span className="text-taupe line-through mr-2">{eur(p.compare_price)}</span>}
              <span className="text-ink">{eur(p.price)}</span>
            </div>
          )}
          <div className="mt-2"><AvailBadge product={p} testId={`avail-${p.id}`} /></div>
        </div>
        {isPurchasable(p) ? (
          <button onClick={() => onAdd(p)} aria-label="Aggiungi al carrello"
            className="shrink-0 w-11 h-11 border border-ink hover:bg-ink hover:text-bone transition-colors flex items-center justify-center"
            data-testid={`add-to-cart-${p.id}`}>
            <Plus size={16} />
          </button>
        ) : (
          <Link to={`/shop/${p.id}`} aria-label="Scopri"
            className="shrink-0 w-11 h-11 border border-hair hover:border-ink transition-colors flex items-center justify-center"
            data-testid={`discover-${p.id}`}>
            <ArrowUpRight size={16} />
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Shop() {
  useSEO({ title: "Shop — Selezione Giovanna Matino Interior", description: "Arredi, complementi e dettagli scelti da Giovanna Matino per dare carattere ai tuoi spazi. Acquisto online sicuro o prezzo esclusivo su richiesta." });
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Tutti");
  const [availFilter, setAvailFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState("new");
  const [shown, setShown] = useState(PAGE);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [apiCategories, setApiCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    api.get("/shop/categories").then((r) => setApiCategories(r.data.map((c) => c.name))).catch(() => {});
  }, []);

  // Arrivo dal banner "Richiedi il prezzo esclusivo": apre direttamente il modulo preventivo
  useEffect(() => {
    if (searchParams.get("preventivo") !== "1") return;
    if (cart.count > 0) {
      setCheckoutOpen(true);
    } else {
      setDrawerOpen(true);
      toast.info("Aggiungi al carrello i prodotti che ti interessano, poi richiedi il prezzo riservato.");
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const categories = useMemo(() => {
    const fromProducts = Array.from(new Set(items.flatMap((i) => (i.categories?.length ? i.categories : [i.category]).filter(Boolean))));
    const base = apiCategories.length ? apiCategories.filter((c) => fromProducts.includes(c)) : fromProducts;
    return ["Tutti", ...base];
  }, [items, apiCategories]);

  const filtered = useMemo(() => {
    let out = items;
    if (filter !== "Tutti") out = out.filter((i) => (i.categories?.length ? i.categories : [i.category]).includes(filter));
    if (availFilter !== "all") out = out.filter((i) => availOf(i) === availFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((i) => i.name.toLowerCase().includes(q) || (i.sku || "").toLowerCase().includes(q));
    }
    const pm = parseFloat(priceMax);
    if (!isNaN(pm) && pm > 0) out = out.filter((i) => i.price <= pm);
    if (sort === "price_asc") out = [...out].sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") out = [...out].sort((a, b) => b.price - a.price);
    else out = [...out].sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0) || (b.created_at || "").localeCompare(a.created_at || ""));
    return out;
  }, [items, filter, availFilter, search, priceMax, sort]);

  const newArrivals = useMemo(() => items.filter((i) => i.is_new).slice(0, 3), [items]);
  const visible = filtered.slice(0, shown);

  const addToCart = (p) => {
    cart.add(p);
    toast.success(`${p.name} aggiunto al carrello`, {
      action: { label: "Vai al carrello", onClick: () => setDrawerOpen(true) },
    });
  };

  return (
    <div data-testid="page-shop">
      {/* Hero editoriale */}
      <section className="relative overflow-hidden bg-ink text-bone">
        <FocalImage settings={settings} prefix="shop_hero" ariaHidden
          pictureClassName="absolute inset-0" className="w-full h-full object-cover opacity-30" />
        <div className="relative container-x pt-12 pb-16 md:pt-16 md:pb-20">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <span className="eyebrow !text-gold">Shop</span>
              <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6">
                La selezione <em className="text-gold font-light">Giovanna Matino.</em>
              </h1>
              <p className="mt-8 text-bone/70 max-w-xl text-lg font-light leading-relaxed">
                Arredi, tessili, illuminazione e complementi scelti con lo stesso rigore con cui progetto le tue case.
                Acquista online in sicurezza o richiedi il prezzo esclusivo riservato ai clienti dello Studio.
              </p>
            </div>
            <CartSheet open={drawerOpen} onOpenChange={setDrawerOpen} onInquiry={() => setCheckoutOpen(true)} dark testId="shop-cart" />
          </div>
        </div>
      </section>

      {/* Nuovi arrivi */}
      {newArrivals.length > 0 && (
        <section className="container-x pt-14" data-testid="shop-new-arrivals">
          <div className="eyebrow text-gold">Nuovi arrivi</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 mt-6">
            {newArrivals.map((p) => <ProductCard key={`new-${p.id}`} p={p} onAdd={addToCart} />)}
          </div>
        </section>
      )}

      {/* Ricerca + filtri + ordinamento */}
      <section className="container-x mt-12 mb-8" data-testid="shop-toolbar">
        <div className="border-y border-hair py-4 grid gap-4 lg:grid-cols-[1fr_auto_auto_auto] items-center">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setShown(PAGE); }} placeholder="Cerca per nome o codice…"
              className="rounded-none pl-9 border-0 bg-transparent focus-visible:ring-0" data-testid="shop-search" />
          </div>
          <select value={availFilter} onChange={(e) => { setAvailFilter(e.target.value); setShown(PAGE); }}
            className="border border-hair bg-white px-3 h-10 text-sm min-h-[44px]" data-testid="shop-filter-availability">
            <option value="all">Tutta la disponibilità</option>
            {Object.entries(AVAIL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input type="number" min={0} value={priceMax} onChange={(e) => { setPriceMax(e.target.value); setShown(PAGE); }}
            placeholder="Prezzo max €" className="rounded-none w-36 min-h-[44px]" data-testid="shop-filter-price" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-hair bg-white px-3 h-10 text-sm min-h-[44px]" data-testid="shop-sort">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 pt-4">
          {categories.map((c) => (
            <button key={c} onClick={() => { setFilter(c); setShown(PAGE); }}
              data-testid={`shop-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 min-h-[44px] text-xs tracking-[0.14em] uppercase transition-colors ${filter === c ? "bg-ink text-bone" : "text-ink/70 hover:text-ink"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Griglia prodotti */}
      <section className="container-x pb-16">
        {loading ? (
          <div className="text-taupe text-center py-24">Caricamento…</div>
        ) : visible.length === 0 ? (
          <div className="text-taupe text-center py-24" data-testid="shop-empty">Nessun prodotto corrisponde alla ricerca.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14" data-testid="shop-grid">
              {visible.map((p) => <ProductCard key={p.id} p={p} onAdd={addToCart} />)}
            </div>
            {filtered.length > shown && (
              <div className="text-center mt-14">
                <button onClick={() => setShown((s) => s + PAGE)} className="btn-outline-ink min-h-[48px]" data-testid="shop-load-more">
                  Carica altri prodotti ({filtered.length - shown} rimanenti)
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {checkoutOpen && <InquiryModal onClose={() => setCheckoutOpen(false)} />}

      {/* Cross-selling Shop the Project */}
      <section className="bg-ink text-bone py-16" data-testid="shop-cross-stp">
        <div className="container-x flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div className="max-w-2xl">
            <span className="eyebrow !text-gold">Shop the Project</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-3">Vuoi essere sicuro che questi prodotti funzionino davvero insieme nel tuo ambiente?</h3>
            <p className="mt-4 text-bone/70 font-light text-sm leading-relaxed">
              Con Shop the Project lo Studio compone per te una Shopping List personalizzata — e i clienti dei servizi aderenti accedono a condizioni esclusive sullo Shop.
            </p>
          </div>
          <Link to="/shop-the-project" className="inline-flex items-center gap-3 px-8 py-3 text-xs tracking-[0.14em] uppercase border border-bone text-bone hover:bg-bone hover:text-ink transition-all shrink-0 min-h-[48px]" data-testid="shop-cta-stp">
            Scopri Shop the Project
          </Link>
        </div>
      </section>

      {/* Bottom section */}
      <section className="bg-white border-t border-hair py-20">
        <div className="container-x grid md:grid-cols-3 gap-10">
          {[
            ["Selezione curata", "Ogni prodotto è scelto personalmente da Giovanna. Solo brand di qualità, materiali onesti."],
            ["Prezzi trasparenti", "Miglior prezzo di mercato garantito. Ogni preventivo include sconto riservato ai cantieri."],
            ["Consegna e assistenza", "Coordiniamo spedizione, consegna e — se serve — installazione da parte di artigiani di fiducia."],
          ].map(([t, d]) => (
            <div key={t}>
              <div className="eyebrow text-gold">Perché acquistare qui</div>
              <h4 className="font-serif text-2xl mt-3">{t}</h4>
              <p className="mt-3 text-sm text-taupe leading-relaxed font-light">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function InquiryModal({ onClose }) {
  const cart = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitInquiry = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const items = cart.items.map(({ product_id, product_name, variant, price, quantity }) => ({ product_id, product_name, variant, price, quantity }));
      await api.post("/inquiries", { ...form, items });
      cart.clear();
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore invio richiesta");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="relative max-w-lg w-full bg-bone p-8 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="inquiry-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 min-w-[44px] min-h-[44px] hover:bg-gold hover:text-bone transition-colors" data-testid="inquiry-close" aria-label="Chiudi">✕</button>
        {done ? (
          <div className="text-center py-6" data-testid="inquiry-success">
            <h3 className="font-serif text-3xl">Richiesta inviata!</h3>
            <p className="text-taupe mt-4 text-sm leading-relaxed">Ti invieremo una proposta personalizzata con il tuo prezzo esclusivo entro 48h.</p>
            <button onClick={onClose} className="btn-ink mt-8" data-testid="inquiry-done-close">Chiudi</button>
          </div>
        ) : (
          <>
            <div className="eyebrow text-gold">Prezzo esclusivo</div>
            <h3 className="font-serif text-3xl mt-2">Richiedi la tua proposta riservata</h3>
            <p className="text-sm text-taupe mt-3">Nessun pagamento online: ricevi un preventivo personalizzato con sconto riservato entro 48h.</p>
            <form onSubmit={submitInquiry} className="mt-6 space-y-4" data-testid="checkout-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome e cognome *</Label>
                  <Input required value={form.name} onChange={set("name")} className="rounded-none mt-1" data-testid="inquiry-name" /></div>
                <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Telefono *</Label>
                  <Input required value={form.phone} onChange={set("phone")} className="rounded-none mt-1" data-testid="inquiry-phone" /></div>
              </div>
              <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Email *</Label>
                <Input type="email" required value={form.email} onChange={set("email")} className="rounded-none mt-1" data-testid="inquiry-email" /></div>
              <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Indirizzo di consegna (per il trasporto)</Label>
                <Input value={form.address} onChange={set("address")} className="rounded-none mt-1" data-testid="inquiry-address" /></div>
              <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note</Label>
                <Textarea rows={2} value={form.notes} onChange={set("notes")} className="rounded-none mt-1" data-testid="inquiry-notes" /></div>
              <div className="text-xs text-taupe">{cart.count} articol{cart.count === 1 ? "o" : "i"} nella richiesta · totale di listino {eur(cart.total)}</div>
              <button type="submit" disabled={busy} className="btn-ink w-full min-h-[48px] disabled:opacity-60" data-testid="inquiry-submit">
                {busy ? "Invio in corso…" : "Invia la richiesta"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
